<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {formatDate, showSuccessMessage} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import {createConfirmDialog} from "vuejs-confirm-dialog";
import router from "@/router";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import {ErrorMessage, Field, Form} from 'vee-validate';
import ContractTypeService from "@/services/ContractTypeService";
import DocumentTemplateService from "@/services/DocumentTemplateService";
import Textarea from "@/components/forms/Textarea.vue";
import Multiselect from "@vueform/multiselect";
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const props = defineProps({
  contractType: {type: Object, required: false},
});

const contractType = ref({name: "", description: "", abbreviation: "", signing_required_document_template_ids: []});

const documentTemplates = ref([]);
const documentTemplatesOptions = computed(() => documentTemplates.value.map(d => ({value: d.id, label: d.name})));

onMounted(() => {
  DocumentTemplateService.getAllActive().then((response) => {
    documentTemplates.value = response;
  });
});

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);

const isActiveNewContractCustomDate = ref(false);

const isActiveNewProductsCustomDate = ref(false);

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', contractType.value);
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

if (props.contractType) {
  contractType.value = {...props.contractType};
  if (props.contractType.is_active_new_contract) {
    isActiveNewContractCustomDate.value = !!(props.contractType.is_active_new_contract_from || props.contractType.is_active_new_contract_to);
    if (props.contractType.is_active_new_contract_from) {
      contractType.value.is_active_new_contract_from = props.contractType.is_active_new_contract_from.slice(0, 16);
    }
    if (props.contractType.is_active_new_contract_to) {
      contractType.value.is_active_new_contract_to = props.contractType.is_active_new_contract_to.slice(0, 16);
    }
  }
  if (props.contractType.is_active_new_products) {
    isActiveNewProductsCustomDate.value = !!(props.contractType.is_active_new_products_from || props.contractType.is_active_new_products_to);
    if (props.contractType.is_active_new_products_from) {
      contractType.value.is_active_new_products_from = props.contractType.is_active_new_products_from.slice(0, 16);
    }
    if (props.contractType.is_active_new_products_to) {
      contractType.value.is_active_new_products_to = props.contractType.is_active_new_products_to.slice(0, 16);
    }
  }
}

function deleteContractType() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('createUpdateContractTypeModal.delete_contract_type'),
    message: t('createUpdateContractTypeModal.delete_contract_type_confirm'),
  });
  onConfirm(() => {
    ContractTypeService.deleteContractType(contractType.value.id).then(() => {
      showSuccessMessage(t('createUpdateContractTypeModal.contract_type_deleted'));
      closeModalWith('cancel')
      router.push({name: 'ContractTypes'});
    });
  });
  reveal();
}

function openModal() {
  isOpen.value = true
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="contractType?.id" as="h3" class="modal-title">
                {{ t('createUpdateContractTypeModal.contract_type_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateContractTypeModal.contract_type_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form ref="form" v-on:submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="contractTypeInput" class="form-label">{{ t('createUpdateContractTypeModal.name') }}</label>
                    <input v-model="contractType.name" id="contractTypeInput" class="form-control" type="text" required/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="contractTypeTextarea" class="form-label">{{ t('createUpdateContractTypeModal.description') }}</label>
                    <textarea v-model="contractType.description" id="contractTypeTextarea" class="form-control"></textarea>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="contractTypeAbbreviationInput" class="form-label">{{ t('createUpdateContractTypeModal.abbreviation') }}</label>
                    <input v-model="contractType.abbreviation" id="contractTypeAbbreviationInput" class="form-control" type="text"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label class="form-label">{{ t('createUpdateContractTypeModal.signing_required_document_templates') }}</label>
                    <Multiselect
                        v-model="contractType.signing_required_document_template_ids"
                        :options="documentTemplatesOptions"
                        :close-on-select="false"
                        :hide-selected="false"
                        :placeholder="t('createUpdateContractTypeModal.signing_required_document_templates_placeholder')"
                        mode="tags"
                    />
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12">
                    <div class="form-check">
                      <label for="isActiveNewContractCheckbox" class="form-label">Aktivní pro vytvoření nové smlouvy</label>
                      <input type="checkbox" v-model="contractType.is_active_new_contract" id="isActiveNewContractCheckbox" class="form-check-input"/></div>
                  </div>
                  <div v-if="contractType.is_active_new_contract" class="col-12">
                    <fieldset class="pb-2">
                      <div class="form-group">
                        <div class="form-check form-switch">
                          <input class="form-check-input" type="checkbox" role="switch" id="switchIsActiveNewContractDate" v-model="isActiveNewContractCustomDate"/>
                          <label class="form-check-label" for="switchIsActiveNewContractDate">Nastavit období</label>
                        </div>
                      </div>

                      <div v-if="isActiveNewContractCustomDate" class="row">
                        <div class="form-group col-6">
                          <label for="isActiveNewContractFromDateInput" class="form-label">Od:</label>
                          <input v-model="contractType.is_active_new_contract_from" id="isActiveNewContractFromDateInput" class="form-control" type="datetime-local"/>
                        </div>
                        <div class="form-group col-6">
                          <label for="isActiveNewContractToDateInput" class="form-label">Do:</label>
                          <input v-model="contractType.is_active_new_contract_to" id="isActiveNewContractToDateInput" class="form-control" type="datetime-local"/>
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12">
                    <div class="form-check">
                      <label for="isActiveNewProductsCheckbox" class="form-label">Aktivní pro přidávání nových produktů</label>
                      <input type="checkbox" v-model="contractType.is_active_new_products" id="isActiveNewProductsCheckbox" class="form-check-input"/>
                    </div>
                  </div>
                  <div v-if="contractType.is_active_new_products" class="col-12 form-group">
                    <fieldset class="pb-2">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchIsActiveNewProductsDate" v-model="isActiveNewProductsCustomDate"/>
                        <label class="form-check-label" for="switchIsActiveNewProductsDate">Nastavit období</label>
                      </div>
                      <div v-if="isActiveNewProductsCustomDate" class="row form-group">
                        <div class="col-6 form-group">
                          <label for="isActiveNewProductsFromDateInput" class="form-label">Od:</label>
                          <input v-model="contractType.is_active_new_products_from" id="isActiveNewProductsFromDateInput" class="form-control" type="datetime-local"/>
                        </div>
                        <div class="col-6 form-group">
                          <label for="isActiveNewProductsToDateInput" class="form-label">Do:</label>
                          <input v-model="contractType.is_active_new_products_to" id="isActiveNewProductsToDateInput" class="form-control" type="datetime-local"/>
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateContractTypeModal.cancel') }}</button>
                <button v-if="contractType?.id" class="btn btn-danger btn-md" @click.prevent="deleteContractType">{{ t('createUpdateContractTypeModal.delete') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateContractTypeModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

